import type { AlertList, Game } from '@growth/client';
import { type ReactNode, Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useApi, useSession } from '../session.tsx';
import { Empty } from './ui.tsx';

const THEME_KEY = 'growth.theme';
type Theme = 'auto' | 'light' | 'dark';

export function applyStoredTheme(): void {
  try {
    const t = localStorage.getItem(THEME_KEY) as Theme | null;
    if (t === 'light' || t === 'dark') document.documentElement.dataset.theme = t;
  } catch {
    // no storage: follow the system
  }
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => (document.documentElement.dataset.theme as Theme | undefined) ?? 'auto');
  const pick = (t: Theme) => {
    setTheme(t);
    if (t === 'auto') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = t;
    try {
      localStorage.setItem(THEME_KEY, t);
    } catch {
      // ignore
    }
  };
  return (
    <div className="segmented" role="group" aria-label="Tema">
      {(['auto', 'light', 'dark'] as const).map((t) => (
        <button key={t} type="button" aria-pressed={theme === t} onClick={() => pick(t)}>
          {t === 'auto' ? 'Auto' : t === 'light' ? 'Claro' : 'Oscuro'}
        </button>
      ))}
    </div>
  );
}

export function useGames() {
  return useApi<Game[]>('games', (api) => api.listGames());
}

/** Open production alerts next to "Alertas" in the menu (refreshed every two minutes). */
function OpenAlerts({ gameId }: { gameId: string }) {
  const r = useApi<AlertList>(`alerts-badge:${gameId}`, (api, signal) => api.alerts(gameId, { status: 'open', environment: 'production', limit: 1 }, signal), { refreshMs: 120_000 });
  const c = r.data?.counts;
  if (!c?.open) return null;
  return (
    <span className={`nav-count${c.critical ? ' critical' : ''}`} title={`${c.open} alerta(s) abierta(s) en producción${c.critical ? `, ${c.critical} crítica(s)` : ''}`}>
      {c.open}
    </span>
  );
}

const ANALYTICS: { path: string; label: string }[] = [
  { path: '', label: 'Resumen' },
  { path: '/diagnostics', label: 'Diagnóstico' },
  { path: '/engagement', label: 'Engagement' },
  { path: '/retention', label: 'Retención' },
  { path: '/activation', label: 'Activación' },
  { path: '/runs', label: 'Runs' },
  { path: '/compare', label: 'Comparar A/B' },
];

const ACQUISITION: { path: string; label: string }[] = [
  { path: '/acquisition', label: 'Adquisición' },
  { path: '/creatives', label: 'Creatividades' },
  { path: '/playables', label: 'Playables' },
  { path: '/creative-insights', label: 'Aprendizaje creativo' },
  { path: '/creative-ai', label: 'IA creativa' },
  { path: '/funnel', label: 'Embudo' },
  { path: '/tests', label: 'Test Lab' },
];

const MONETIZATION: { path: string; label: string }[] = [
  { path: '/monetization', label: 'Monetización' },
  { path: '/ltv', label: 'LTV y ROAS' },
  { path: '/economics', label: 'Unit economics' },
];

const EXPERIMENT: { path: string; label: string }[] = [
  { path: '/experiments', label: 'Experimentos' },
  { path: '/remote-config', label: 'Remote Config' },
];

const OPERATE: { path: string; label: string }[] = [
  { path: '/alerts', label: 'Alertas' },
  { path: '/reports', label: 'Informes' },
  { path: '/live', label: 'Eventos en vivo' },
  { path: '/health', label: 'Data health' },
  { path: '/settings', label: 'Configuración' },
];

export function Layout() {
  const { gameId } = useParams();
  const { isAdmin, logout } = useSession();
  const games = useGames();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [location.pathname]);

  // Filters (search string) follow the user across analytics pages; per-page state (tabs, run ids) does not.
  const search = location.search;
  const link = (path: string) => ({ pathname: `/g/${gameId}${path}`, search });
  const activeGames = (games.data ?? []).filter((g) => !g.archived_at);

  return (
    <div className="shell">
      <aside className={`sidebar${open ? ' open' : ''}`}>
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <Link to="/" className="brand" style={{ textDecoration: 'none' }}>
            <img src="/favicon.svg" width={26} height={26} alt="" />
            <span>
              Game Growth OS
              <small>Hive Interactive</small>
            </span>
          </Link>
          <button type="button" className="btn small menu-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            Menú
          </button>
        </div>
        <label className="field">
          <span>Juego</span>
          <select className="input" value={gameId ?? ''} onChange={(e) => e.target.value && navigate(`/g/${e.target.value}${search}`)}>
            {!gameId && <option value="">Elige un juego…</option>}
            {activeGames.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
            {gameId && !activeGames.some((g) => g.id === gameId) && <option value={gameId}>{gameId}</option>}
          </select>
        </label>
        <nav className="nav" aria-label="Secciones">
          {gameId && (
            <>
              <div className="nav-title">Analítica</div>
              {ANALYTICS.map((n) => (
                <NavLink key={n.path} to={link(n.path)} end={n.path === ''}>
                  {n.label}
                </NavLink>
              ))}
              <div className="nav-title">Adquisición</div>
              {ACQUISITION.map((n) => (
                <NavLink key={n.path} to={link(n.path)}>
                  {n.label}
                </NavLink>
              ))}
              <div className="nav-title">Monetización</div>
              {MONETIZATION.map((n) => (
                <NavLink key={n.path} to={link(n.path)}>
                  {n.label}
                </NavLink>
              ))}
              <div className="nav-title">Experimentación</div>
              {EXPERIMENT.map((n) => (
                <NavLink key={n.path} to={link(n.path)}>
                  {n.label}
                </NavLink>
              ))}
              <div className="nav-title">Operación</div>
              {OPERATE.map((n) => (
                <NavLink key={n.path} to={n.path === '/settings' ? `/g/${gameId}/settings` : link(n.path)}>
                  {n.label}
                  {n.path === '/alerts' && <OpenAlerts gameId={gameId} />}
                </NavLink>
              ))}
            </>
          )}
          <div className="nav-title">Plataforma</div>
          <NavLink to="/games" end>
            Juegos
          </NavLink>
          <NavLink to="/admin">Tokens y jobs</NavLink>
          <a href="/docs" target="_blank" rel="noreferrer">
            API (OpenAPI) ↗
          </a>
        </nav>
        <div className="sidebar-footer">
          <ThemeToggle />
          <span>Sesión: {isAdmin ? 'admin' : 'solo lectura'}</span>
          <button type="button" className="btn small" onClick={logout}>
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="main">
        <Suspense fallback={<Empty>Cargando…</Empty>}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}

export function PageHeader({ title, sub, right }: { title: ReactNode; sub?: ReactNode; right?: ReactNode }) {
  return (
    <header className="topbar">
      <div>
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {right && <div className="row">{right}</div>}
    </header>
  );
}
